/*
  Yashi — ajouter au panier sans quitter la page.

  Demande de Simon, 19 septembre 2026 : quand on ajoute un produit, on voit
  l'animation et le chiffre du panier changer, mais on reste ou l'on est. On
  va au panier quand on veut payer.

  Les boutons d'achat des fiches produit d'Horizon le font deja. Nos
  formulaires maison, eux, postaient vers /cart/add, et Shopify renvoyait
  alors vers la page panier. Ils portent tous l'attribut data-yashi-ajout :
    - « Ajouter au panier + » des vitrines de la page Boutique
      (snippets/yashi-vitrine-signatures, -murmures, -accessoires) ;
    - les suggestions des fiches (snippets/yashi-suggestions) ;
    - les cartes des pages collection (snippets/yashi-ajout-carte) ;
    - le bouton du duo Murmures (snippets/yashi-ajout-duo).

  Ce script prend leur envoi en charge. Il ajoute par /cart/add.js, puis :
    - fait voler la vignette du produit vers l'icone du panier (l'element
      fly-to-cart d'Horizon, le meme que sur les fiches) ;
    - emet l'evenement standard shopify:cart:lines-update, comme le
      formulaire des fiches : l'icone du panier met son chiffre a jour avec
      son animation (assets/cart-icon.js), et yashi-cadeau-duo ajoute les
      allumettes offertes si le duo Murmures est reuni ;
    - affiche « Ajoute » sur le bouton pendant deux secondes, et l'annonce
      aux lecteurs d'ecran.
  En cas de refus de Shopify (stock epuise par exemple), son message s'affiche
  sous le bouton ; en cas de panne reseau, le formulaire repart par la voie
  classique.

  Sur la page panier elle-meme, rien n'est intercepte : l'envoi classique
  recharge la page, ce qui remet lignes et total a jour.
  Charge par layout/theme.liquid.
*/

import { CartLinesUpdateEvent } from '@shopify/events';

const SELECTEUR = 'form[data-yashi-ajout]';

// Horizon declare ses reglages en constante globale (const Theme, snippets/scripts.liquid) :
// elle n'est pas rattachee a window, on la lit donc par son nom.
const reglages = () => (typeof Theme === 'undefined' ? null : Theme);
const libelleAjoute = () => reglages()?.translations?.added || 'Ajouté';
const DUREE_CONFIRMATION = 2000;
const DUREE_ERREUR = 6000;

/** Lignes du formulaire : un produit (id, quantity) ou plusieurs (items[i][id]). */
function lignesDuFormulaire(donnees) {
  const lignes = [];
  if (donnees.get('id')) {
    lignes.push({ merchandiseId: String(donnees.get('id')), quantity: Number(donnees.get('quantity')) || 1 });
  }
  for (let i = 0; donnees.has(`items[${i}][id]`); i++) {
    lignes.push({
      merchandiseId: String(donnees.get(`items[${i}][id]`)),
      quantity: Number(donnees.get(`items[${i}][quantity]`)) || 1,
    });
  }
  return lignes;
}

/** Image qui s'envole vers le panier : celle du formulaire, sinon celle de sa carte. */
function imageDuFormulaire(formulaire) {
  if (formulaire.dataset.yashiImage) return formulaire.dataset.yashiImage;
  const carte = formulaire.closest('article, product-card, li');
  const image = carte?.querySelector('img');
  return image ? image.currentSrc || image.src : '';
}

function faireVoler(bouton, image) {
  const icone = document.querySelector('.header-actions__cart-icon');
  if (!icone || !bouton || !image || !customElements.get('fly-to-cart')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const vol = document.createElement('fly-to-cart');
  vol.classList.add('fly-to-cart--main');
  vol.style.setProperty('background-image', `url("${image}")`);
  vol.style.setProperty('--start-opacity', '0');
  vol.source = bouton;
  vol.destination = icone;
  document.body.appendChild(vol);
}

/** Zone lue par les lecteurs d'ecran, creee une fois. */
function annoncer(texte) {
  let zone = document.getElementById('yashi-annonce-panier');
  if (!zone) {
    zone = document.createElement('p');
    zone.id = 'yashi-annonce-panier';
    zone.className = 'visually-hidden';
    zone.setAttribute('aria-live', 'polite');
    document.body.appendChild(zone);
  }
  zone.textContent = '';
  requestAnimationFrame(() => {
    zone.textContent = texte;
  });
}

function confirmer(bouton) {
  if (!bouton) return;
  const contenu = bouton.innerHTML;
  bouton.textContent = libelleAjoute();
  setTimeout(() => {
    bouton.innerHTML = contenu;
    bouton.disabled = false;
    bouton.removeAttribute('aria-busy');
  }, DUREE_CONFIRMATION);
}

function signalerErreur(formulaire, bouton, message) {
  formulaire.parentElement?.querySelector('.yashi-ajout-erreur')?.remove();
  const avis = document.createElement('p');
  avis.className = 'yashi-ajout-erreur';
  avis.setAttribute('role', 'alert');
  avis.textContent = message;
  formulaire.insertAdjacentElement('afterend', avis);
  setTimeout(() => avis.remove(), DUREE_ERREUR);
  if (bouton) {
    bouton.disabled = false;
    bouton.removeAttribute('aria-busy');
  }
}

async function lirePanier() {
  const reponse = await fetch(`${reglages().routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return reponse.json();
}

document.addEventListener('submit', async (evenement) => {
  const formulaire = evenement.target;
  if (!(formulaire instanceof HTMLFormElement) || !formulaire.matches(SELECTEUR)) return;
  if (!reglages()?.routes?.cart_add_url) return;
  // Sur la page panier, l'envoi classique recharge la page : c'est ce qu'on veut.
  if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;

  evenement.preventDefault();
  if (formulaire.dataset.yashiEnvoi === 'oui') return;
  formulaire.dataset.yashiEnvoi = 'oui';

  const bouton = evenement.submitter || formulaire.querySelector('[type="submit"]');
  if (bouton) {
    bouton.disabled = true;
    bouton.setAttribute('aria-busy', 'true');
  }

  const donnees = new FormData(formulaire);
  const lignes = lignesDuFormulaire(donnees);
  const quantite = lignes.reduce((total, ligne) => total + ligne.quantity, 0);
  const attente = CartLinesUpdateEvent.createPromise();
  formulaire.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: lignes, promise: attente.promise })
  );

  try {
    const reponse = await fetch(reglages().routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: donnees,
    });
    const resultat = await reponse.json();
    const panier = await lirePanier();
    const refus = !reponse.ok || Boolean(resultat.status);

    attente.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
      detail: { items: panier.items, source: 'yashi-ajout-panier', itemCount: quantite, didError: refus },
    });

    if (refus) {
      signalerErreur(formulaire, bouton, resultat.description || resultat.message || '');
      return;
    }

    faireVoler(bouton, imageDuFormulaire(formulaire));
    confirmer(bouton);
    annoncer(libelleAjoute());
  } catch (erreur) {
    // Panne reseau ou reponse illisible : la voie classique prend le relais.
    attente.reject(erreur);
    formulaire.submit();
  } finally {
    delete formulaire.dataset.yashiEnvoi;
  }
});
